/**
 * RFM segment card — non-clickable tile showing an RFM-scored segment.
 *
 * Extracted from Segments.tsx during the Maintainability Sprint 2026-04-19
 * (Task 1.1 step 1.1.6). Behavior is preserved byte-for-byte; coverage in
 * ../__tests__/Segments.test.tsx ("clicking RFM tab shows RFM segment cards"
 * plus the name / description / count / revenue rendering assertions).
 */

import { Users } from "lucide-react";
import { formatFCFA } from "@cieda/utils";
import type { RfmSegmentRow } from "@/lib/admin-api/crm";
import { RFM_STYLES, DEFAULT_STYLE } from "./constants";

interface RfmSegmentCardProps {
  segment: RfmSegmentRow;
}

export default function RfmSegmentCard({ segment }: RfmSegmentCardProps) {
  const style = RFM_STYLES[segment.key] ?? DEFAULT_STYLE;

  return (
    <div className={`rounded-xl border p-5 ${style.bg} ${style.border}`}>
      <div className="flex items-start gap-3">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${style.iconBg}`}
        >
          <Users className={`h-5 w-5 ${style.text}`} />
        </div>
      </div>

      <div className="mt-3">
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${style.bg} ${style.text}`}
        >
          {segment.name}
        </span>
        <p className="mt-1.5 text-sm text-stone-600">{segment.description}</p>
      </div>

      <div className="mt-4 flex items-end justify-between border-t border-black/5 pt-3">
        <div>
          <p className="text-xs text-stone-500">Clients</p>
          <p className={`text-xl font-display font-semibold ${style.text}`}>
            {segment.customer_count.toLocaleString("fr-SN")}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-stone-500">Revenu moyen</p>
          <p className="text-sm font-semibold text-stone-800">
            {formatFCFA(segment.avg_revenue_fcfa)}
          </p>
        </div>
      </div>
    </div>
  );
}
